import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface AnexoRecord {
  id: string;
  condominio_id: string;
  nome: string;
  tipo?: string;
  formato?: string;
  data_upload: string;
  tamanho?: string;
}

export default function AnexosTab() {
  const [anexos, setAnexos] = useState<AnexoRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AnexoRecord | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const fetchAnexos = async () => {
    setLoading(true);
    setError(null);
    try {
      const urlParts = window.location.pathname.split('/');
      const condoId = urlParts[2];
      const { data, error: fErr } = await supabase
        .from('anexos')
        .select('*')
        .eq('condominio_id', condoId)
        .order('data_upload', { ascending: false });
      if (fErr) setError(fErr.message);
      else setAnexos((data || []) as AnexoRecord[]);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnexos();
  }, []);

  const tipoLabelColor = (tipo?: string) => {
    const map: Record<string, string> = {
      Licença: 'bg-accent-100 text-accent-900 border-accent-300',
      Contrato: 'bg-secondary-100 text-secondary-900 border-secondary-200',
      Prestador: 'bg-primary-50 text-primary-700 border-primary-200',
      Funcionário: 'bg-primary-50 text-primary-700 border-primary-200',
    };
    if (tipo && map[tipo]) return map[tipo];
    if (tipo?.includes('Manutenção'))
      return 'bg-secondary-100 text-secondary-900 border-secondary-200';
    if (tipo?.includes('Construção')) return 'bg-accent-100 text-accent-900 border-accent-300';
    return 'bg-background-100 text-foreground-700 border-background-300';
  };

  const formatoIcon = (formato?: string) => {
    const map: Record<string, string> = {
      PDF: 'ri-file-pdf-line',
      XLSX: 'ri-file-excel-2-line',
      CSV: 'ri-file-excel-2-line',
      CAD: 'ri-file-settings-line',
      DWG: 'ri-file-settings-line',
    };
    return (formato && map[formato.toUpperCase()]) || 'ri-file-line';
  };

  const formatoColor = (formato?: string) => {
    const map: Record<string, string> = {
      PDF: 'text-red-600',
      XLSX: 'text-green-600',
      CSV: 'text-green-600',
      CAD: 'text-amber-600',
      DWG: 'text-amber-600',
    };
    return (formato && map[formato.toUpperCase()]) || 'text-foreground-500';
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase.from('anexos').delete().eq('id', deleteTarget.id);
    if (dErr) {
      showToast(`Erro: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }
    setAnexos((prev) => prev.filter((a) => a.id !== deleteTarget.id));
    showToast(`"${deleteTarget.nome}" excluído com sucesso.`, 'success');
    setDeleteTarget(null);
  };

  const tiposDisponiveis = [
    ...new Set(anexos.map((a) => a.tipo).filter(Boolean)),
  ].sort() as string[];
  const filtrados = anexos.filter((a) => {
    const matchNome = a.nome.toLowerCase().includes(busca.toLowerCase());
    const matchTipo = filtroTipo ? a.tipo === filtroTipo : true;
    return matchNome && matchTipo;
  });

  const formatData = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={fetchAnexos}
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Anexos do Condomínio</h3>
          <p className="text-sm text-foreground-500 mt-0.5">
            {filtrados.length} de {anexos.length} arquivo(s)
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
          <i className="ri-upload-line"></i>Adicionar arquivo
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Buscar por nome do arquivo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
          />
        </div>
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          className="px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm min-w-[180px]"
        >
          <option value="">Todos os tipos</option>
          {tiposDisponiveis.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {anexos.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-attachment-2 text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Nenhum anexo cadastrado</p>
          <p className="text-sm text-foreground-500 mt-1">
            Clique em "Adicionar arquivo" para começar.
          </p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Nome do arquivo
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Tipo
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Formato
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Data do upload
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Tamanho
                  </th>
                  <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {filtrados.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-foreground-400">
                      Nenhum arquivo encontrado.
                    </td>
                  </tr>
                ) : (
                  filtrados.map((anexo) => (
                    <tr key={anexo.id} className="hover:bg-background-100/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center ${formatoColor(anexo.formato)}`}
                          >
                            <i className={`${formatoIcon(anexo.formato)} text-lg`}></i>
                          </div>
                          <span
                            className="text-sm font-medium text-foreground-800 truncate max-w-[280px]"
                            title={anexo.nome}
                          >
                            {anexo.nome}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${tipoLabelColor(anexo.tipo)}`}
                        >
                          {anexo.tipo || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground-700">
                          {anexo.formato || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">
                          {formatData(anexo.data_upload)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">{anexo.tamanho || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() =>
                              showToast(`Download de "${anexo.nome}" iniciado.`, 'info')
                            }
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                            title="Download"
                          >
                            <i className="ri-download-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeleteTarget(anexo)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir permanentemente o arquivo{' '}
              <strong>{deleteTarget.nome}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-background-50 text-foreground-800 border border-background-200/70'}`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : toast.type === 'error' ? 'ri-close-circle-line text-red-600' : 'ri-information-line text-primary-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
